/**
 * @file Submission read-only / review UI components — CollapsiblePanel.tsx.
 */

import React, { useId } from "react";
import "./CollapsiblePanel.css";

/** Type definition — Collapsible Panel Variant. */
export type CollapsiblePanelVariant = "section" | "subsection";

/** Type definition — Collapsible Panel Props. */
export type CollapsiblePanelProps = {
  title: string;
  description?: string;
  children: React.ReactNode;
  variant?: CollapsiblePanelVariant;
  /** Controlled expanded state (for expand/collapse all). */
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  panelId?: string;
  className?: string;
};

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
  title,
  description,
  children,
  variant = "section",
  expanded: expandedProp,
  defaultExpanded = true,
  onExpandedChange,
  panelId,
  className = "",
}) => {
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const autoId = useId();
  const headerId = panelId ? `${panelId}-header` : `collapsible-${autoId}-header`;
  const bodyId = panelId ? `${panelId}-body` : `collapsible-${autoId}-body`;

  const setExpanded = (next: boolean) => {
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  const toggle = () => setExpanded(!expanded);

  const onHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <section
      className={`collapsible-panel collapsible-panel--${variant} ${expanded ? "is-expanded" : "is-collapsed"} ${className}`.trim()}
      data-panel-id={panelId}
    >
      <button
        type="button"
        id={headerId}
        className="collapsible-panel__header"
        onClick={toggle}
        onKeyDown={onHeaderKeyDown}
        aria-expanded={expanded}
        aria-controls={bodyId}
      >
        <span className="collapsible-panel__header-text">
          <span className="collapsible-panel__title">{title}</span>
          {description ? (
            <span className="collapsible-panel__description">{description}</span>
          ) : null}
        </span>
        <span
          className={`collapsible-panel__chevron ${expanded ? "is-open" : ""}`}
          aria-hidden
        />
      </button>
      {expanded ? (
        <div
          id={bodyId}
          className="collapsible-panel__body"
          role="region"
          aria-labelledby={headerId}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
};

/** React component — Collapsible Panel. */
export default CollapsiblePanel;
